/**
 * Plain-text excerpt of a Markdown ticket problem for the board card.
 *
 * The card is a glance surface: it gets a few muted lines that say what the ticket is
 * about, and the drawer renders the full Markdown. Flattening here (rather than clamping a
 * rendered tree) keeps headings, task boxes, and fenced code from fighting the title for
 * weight, and keeps soft line breaks from running two authored lines into one.
 */

const CARD_EXCERPT_MAX = 220;

export function cardExcerpt(source: string, max = CARD_EXCERPT_MAX): string {
  let text = source.replace(/\r\n?/g, '\n').replace(/^\s*#{1,6}\s+(?:Problem|Brief)\s*\n/i, '');

  // Fenced code collapses to a marker; nobody reads a code block on a card.
  text = text.replace(/```[^\n]*\n[\s\S]*?```/g, ' [code] ');
  text = text.replace(/~~~[^\n]*\n[\s\S]*?~~~/g, ' [code] ');

  const lines = text.split('\n').map((raw) => {
    let line = raw.trim();
    line = line.replace(/^#{1,6}\s+/, '');
    line = line.replace(/^>\s?/, '');
    line = line.replace(/^([-*+]|\d+[.)])\s+/, '');
    line = line.replace(/^\[( |x|X)\]\s+/, '');
    line = line.replace(/^(?:[-*_]\s*){3,}$/, '');
    line = line.replace(/^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?$/, '');
    line = line.replace(/\|/g, ' ');
    return line;
  });

  let flat = lines.filter((line) => line.length > 0).join(' · ');
  flat = flat
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/<[^>\s][^>]*>/g, '')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/(\*\*|__)(?=\S)([\s\S]*?\S)\1/g, '$2')
    .replace(/(\*|_)(?=\S)([^*_]*?\S)\1/g, '$2')
    .replace(/~~(?=\S)([\s\S]*?\S)~~/g, '$1')
    .replace(/\\([\\`*_{}[\]()#+\-.!|>~])/g, '$1')
    .replace(/\s+/g, ' ')
    .replace(/(\s·\s)+/g, ' · ')
    .replace(/^\s*·\s*|\s*·\s*$/g, '')
    .trim();

  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max);
  const lastBreak = cut.lastIndexOf(' ');
  const head = (lastBreak > max * 0.6 ? cut.slice(0, lastBreak) : cut).replace(/[\s·,;:(-]+$/, '');
  return `${head}…`;
}
